import api from '../../utils/api';

export type AppKey = 'user' | 'plus';
export type UpdateType = 'optional' | 'force';
export type AppPlatform = 'android' | 'ios';

export interface PlatformVersionConfig {
  latestVersion: string;
  minRequiredVersion: string;
  latestVersionCode: number;
  minRequiredVersionCode: number;
  updateType: UpdateType;
  storeUrl: string;
  disableUpdateCheck: boolean;
}

export interface AppConfigDto {
  configId: number | null;
  appKey: AppKey | null;
  latestVersion: string;
  minRequiredVersion: string;
  latestVersionCode: number;
  minRequiredVersionCode: number;
  updateType: UpdateType;
  updateMessage: string | null;
  playstoreUrl: string | null;
  appstoreUrl: string | null;
  android?: PlatformVersionConfig;
  ios?: PlatformVersionConfig;
  maintenanceMode: boolean;
  disableUpdateCheck?: boolean;
  disableUpdateCheckAndroid?: boolean;
  disableUpdateCheckIos?: boolean;
  updatedAt: string | null;
}

export interface AppConfigUpdatePayload {
  android: PlatformVersionConfig;
  ios: PlatformVersionConfig;
  updateMessage: string | null;
  maintenanceMode: boolean;
}

export async function getAppConfig(appKey: AppKey): Promise<AppConfigDto> {
  const response = await api.get<AppConfigDto>('/master-admin/app-config', {
    params: { appKey },
  });
  return { ...response.data, appKey: response.data.appKey ?? appKey };
}

export async function updateAppConfig(
  appKey: AppKey,
  data: AppConfigUpdatePayload
): Promise<AppConfigDto> {
  const response = await api.put<AppConfigDto>(
    '/master-admin/app-config',
    { ...data, appKey },
    { params: { appKey } }
  );
  return response.data;
}

export interface SendUpdateNotificationResponse {
  success: boolean;
  message: string;
  appKey?: AppKey;
  totalTarget: number;
  successCount: number;
  failureCount: number;
  skippedUpToDate?: number;
  android?: { totalTarget?: number; successCount?: number; failureCount?: number; skippedUpToDate?: number };
  ios?: { totalTarget?: number; successCount?: number; failureCount?: number; skippedUpToDate?: number };
}

export async function sendUpdateNotification(
  appKey: AppKey
): Promise<SendUpdateNotificationResponse> {
  // Empty object (not `null`) — Cloud Run returns 411 without Content-Length, which
  // surfaces in the browser as a CORS error because that response has no ACAO header.
  const response = await api.post<SendUpdateNotificationResponse>(
    '/master-admin/app-config/send-update-notification',
    {},
    {
      params: { appKey },
      // FCM multicast to all devices can take several minutes.
      timeout: 10 * 60 * 1000,
    }
  );
  return response.data;
}

export interface UpdateNotificationRecord {
  id: string;
  appKey?: AppKey;
  version: string | null;
  versionCode: number | null;
  androidVersion?: string | null;
  androidVersionCode?: number | null;
  iosVersion?: string | null;
  iosVersionCode?: number | null;
  updateMessage: string | null;
  sentAt: string | null;
  totalTarget: number;
  successCount: number;
  failureCount: number;
  skippedUpToDate?: number;
}

export interface UpdateNotificationHistoryResponse {
  success: boolean;
  data: UpdateNotificationRecord[];
  count: number;
}

export async function getUpdateNotificationHistory(
  limit?: number,
  appKey?: AppKey
): Promise<UpdateNotificationHistoryResponse> {
  const params: { limit?: number; appKey?: AppKey } = {};
  if (limit != null) params.limit = limit;
  if (appKey) params.appKey = appKey;
  const response = await api.get<UpdateNotificationHistoryResponse>(
    '/master-admin/app-config/update-notification-history',
    { params }
  );
  return response.data;
}

export const APP_VERSION_LABELS: Record<AppKey, string> = {
  user: 'Vedika.Health (User App)',
  plus: 'Vedika.Health Plus (Service Provider App)',
};

/** Default Play Store listing when MA field is empty */
export const DEFAULT_PLAYSTORE_URLS: Record<AppKey, string> = {
  user: 'https://play.google.com/store/apps/details?id=health.vedika.app',
  plus: 'https://play.google.com/store/apps/details?id=health.vedika.plus.app',
};

/** Default App Store listing when MA field is empty */
export const DEFAULT_APPSTORE_URLS: Record<AppKey, string> = {
  user: 'https://apps.apple.com/in/app/vedika-health/id6783773712',
  plus: 'https://apps.apple.com/in/app/vedika-health-plus/id6785399959',
};

export function emptyPlatformConfig(
  appKey: AppKey,
  platform: AppPlatform
): PlatformVersionConfig {
  return {
    latestVersion: '1.0.0',
    minRequiredVersion: '1.0.0',
    latestVersionCode: 1,
    minRequiredVersionCode: 1,
    updateType: 'optional',
    storeUrl:
      platform === 'ios' ? DEFAULT_APPSTORE_URLS[appKey] : DEFAULT_PLAYSTORE_URLS[appKey],
    disableUpdateCheck: false,
  };
}

export interface LiveStorePlatformVersion {
  latestVersion: string | null;
  latestVersionCode: number | null;
  storeUrl?: string | null;
  versionSource?: string | null;
  buildSource?: string | null;
  note?: string | null;
}

export interface LiveStoreVersionsResponse {
  success: boolean;
  data: {
    appKey: AppKey;
    android: LiveStorePlatformVersion;
    ios: LiveStorePlatformVersion;
    errors?: {
      androidVersion?: string | null;
      iosVersion?: string | null;
    };
  };
}

export async function getLiveStoreVersions(appKey: AppKey): Promise<LiveStoreVersionsResponse> {
  const response = await api.get<LiveStoreVersionsResponse>('/master-admin/app-config/live-versions', {
    params: { appKey },
    timeout: 20000,
  });
  return response.data;
}
